import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Chip, Divider } from '@mui/material';
import { CheckCircle, Edit, CloudUpload, History, ArrowForward, LockOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../config/axiosConfig';

const steps = [
  {
    label: 'Aceptar Cotización',
    description: 'Revisa y acepta una cotización para comenzar el proceso',
    route: '/perfil-riesgo/historial',
    icon: <History sx={{ fontSize: 28 }} />,
  },
  {
    label: 'Llenar Información',
    description: 'Programa la fecha y hora de visita e ingresa los datos de tu Unidad Interna de Protección Civil',
    route: '/perfil-riesgo/llenar-informacion',
    icon: <Edit sx={{ fontSize: 28 }} />,
  },
  {
    label: 'Subir Archivos',
    description: 'Adjunta los documentos requeridos para tu expediente',
    route: '/perfil-riesgo/subir-archivos',
    icon: <CloudUpload sx={{ fontSize: 28 }} />,
  },
];

const JourneyStepper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasAcceptedQuotation, setHasAcceptedQuotation] = useState(false);
  const [hasCompletedInfo, setHasCompletedInfo] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Un ente está "completo" si tiene los mismos datos que pide LlenarInformacion.jsx:
    // persona que acompaña, fecha y hora de inspección, y detalle de EPP si se requiere.
    const isEntityComplete = (e) => {
      const basicComplete = !!(e.inspectorContact && e.inspectionDate && e.inspectionTime);
      const ppeComplete = !e.requiresPpe || !!e.ppeDetails;
      return basicComplete && ppeComplete;
    };

    const checkProgress = async () => {
      try {
        const userRes = await api.get('/users/me');
        const companyId = userRes.data?.company?.id;

        if (!companyId) {
          setHasAcceptedQuotation(false);
          setHasCompletedInfo(false);
          return;
        }

        const quotationsRes = await api.get('/quotations');
        const all = Array.isArray(quotationsRes.data) ? quotationsRes.data : [];
        // Solo las cotizaciones de ESTA empresa — antes se revisaba el sistema completo.
        const mine = all.filter(q => q.companyId === companyId);
        const accepted = mine.filter(q => q.status === 'ACCEPTED');
        setHasAcceptedQuotation(accepted.length > 0);

        if (accepted.length === 0) {
          setHasCompletedInfo(false);
          return;
        }

        const entityLists = await Promise.all(
          accepted.map(q => api.get(`/quotations/${q.id}/entities`).then(r => r.data).catch(() => []))
        );
        const allEntities = entityLists.flat().filter(Boolean);

        setHasCompletedInfo(allEntities.length > 0 && allEntities.every(isEntityComplete));
      } catch (e) {
        console.error('Error verificando progreso:', e);
      } finally {
        setLoading(false);
      }
    };
    checkProgress();
  }, [location.pathname]);

  const activeStep = steps.findIndex(s => s.route === location.pathname);
  const currentStep = activeStep >= 0 ? activeStep : 0;

  const isCompleted = (index) => {
    if (index === 0) return hasAcceptedQuotation;
    if (index === 1) return hasCompletedInfo;
    return false;
  };

  const isEnabled = (index) => {
    if (index === 0) return true;
    if (index === 1) return hasAcceptedQuotation;
    if (index === 2) return hasAcceptedQuotation;
    return false;
  };

  const nextStep = steps[currentStep + 1];
  const nextEnabled = nextStep && isEnabled(currentStep + 1);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #e8eaf6',
        overflow: 'hidden',
        mb: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>
          Tu proceso de incorporación
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Completa los siguientes pasos para finalizar tu registro
        </Typography>
      </Box>

      {/* Steps */}
      <Box sx={{ display: 'flex', position: 'relative' }}>
        {steps.map((step, index) => {
          const completed = isCompleted(index);
          const active = index === currentStep;
          const enabled = isEnabled(index);
          const isLast = index === steps.length - 1;

          const color = completed ? '#43e97b' : active ? '#667eea' : '#bdbdbd';
          const bgColor = completed ? '#f1fdf7' : active ? '#f8f9ff' : '#fafafa';
          const borderColor = completed ? '#43e97b' : active ? '#667eea' : '#e0e0e0';

          return (
            <React.Fragment key={step.label}>
              <Box
                onClick={() => enabled && navigate(step.route)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  px: 2,
                  py: 3,
                  bgcolor: bgColor,
                  borderTop: `3px solid ${borderColor}`,
                  cursor: enabled ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  '&:hover': enabled ? { bgcolor: active ? '#f0f1ff' : completed ? '#e8fdf0' : '#f5f5f5' } : {},
                  position: 'relative',
                }}
              >
                {/* Badge de estado */}
                {active && (
                  <Chip
                    label="Estás aquí"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: '#667eea',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 20,
                    }}
                  />
                )}
                {completed && !active && (
                  <Chip
                    label="Completado"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: '#e8fdf0',
                      color: '#2e7d32',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 20,
                      border: '1px solid #43e97b',
                    }}
                  />
                )}
                {!enabled && !active && (
                  <LockOutlined sx={{ position: 'absolute', top: 12, right: 12, fontSize: 16, color: '#bdbdbd' }} />
                )}

                {/* Icono */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    border: `2px solid ${color}`,
                    color,
                    mb: 1.5,
                    boxShadow: active ? `0 4px 16px ${color}40` : completed ? `0 4px 12px ${color}30` : 'none',
                  }}
                >
                  {completed ? <CheckCircle sx={{ fontSize: 32 }} /> : step.icon}
                </Box>

                {/* Número de paso */}
                <Typography variant="caption" sx={{ color, fontWeight: 700, mb: 0.5 }}>
                  Paso {index + 1}
                </Typography>

                {/* Título */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: active ? 800 : 600,
                    color: active ? '#667eea' : completed ? '#2e7d32' : '#757575',
                    textAlign: 'center',
                    fontSize: active ? '0.95rem' : '0.875rem',
                  }}
                >
                  {step.label}
                </Typography>

                {/* Descripción — solo en el paso activo */}
                {active && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', textAlign: 'center', mt: 0.5, maxWidth: 160, lineHeight: 1.3 }}
                  >
                    {step.description}
                  </Typography>
                )}
              </Box>

              {/* Separador */}
              {!isLast && (
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fafafa', borderTop: '3px solid #e0e0e0' }}>
                  <ArrowForward sx={{ color: '#e0e0e0', fontSize: 20 }} />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Footer — siguiente paso */}
      {nextStep && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fafafa' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Siguiente paso
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: nextEnabled ? '#667eea' : '#bdbdbd' }}>
                {nextStep.label}
              </Typography>
              {!nextEnabled && (
                <Typography variant="caption" sx={{ color: '#bdbdbd' }}>
                  Completa el paso actual para continuar
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={!nextEnabled}
              onClick={() => navigate(nextStep.route)}
              sx={{
                background: nextEnabled
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : undefined,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
              }}
            >
              Ir a {nextStep.label}
            </Button>
          </Box>
        </>
      )}

      {/* Footer — completado */}
      {!nextStep && hasCompletedInfo && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f1fdf7' }}>
            <CheckCircle sx={{ color: '#43e97b' }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              ¡Proceso completado! Todos los pasos han sido finalizados.
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default JourneyStepper;
